/**
 * 用户列表页面
 */

import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Input,
  Select,
  Tag,
  Typography,
  message,
  Tooltip,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  StopOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import { User, listUsers } from '../api/users';
import { formatBeijingTimeShort } from '../utils/time';

const { Title } = Typography;

const UserList: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchText, setSearchText] = useState('');
  const [country, setCountry] = useState<string>();
  const [isBlocked, setIsBlocked] = useState<string>();
  const [creditAnomaly, setCreditAnomaly] = useState<string>();

  /**
   * 加载用户列表
   */
  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await listUsers({
        page,
        page_size: pageSize,
        country: country || undefined,
        is_blocked: isBlocked === 'true' ? true : isBlocked === 'false' ? false : undefined,
        search: searchText || undefined,
        credit_anomaly: creditAnomaly === 'true' ? true : creditAnomaly === 'false' ? false : undefined,
      });

      if (response.success) {
        setUsers(response.data.users);
        setTotal(response.data.pagination.total);
      }
    } catch (error) {
      console.error('加载用户列表失败:', error);
      message.error('加载失败');
    } finally {
      setLoading(false);
    }
  };

  // 初始加载和参数变化时重新加载
  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, country, isBlocked, creditAnomaly]);

  /**
   * 搜索处理
   */
  const handleSearch = () => {
    setPage(1);
    loadUsers();
  };

  /**
   * 表格列定义
   */
  const columns: ColumnsType<User> = [
    {
      title: 'User ID',
      dataIndex: 'support_id',
      key: 'support_id',
      width: 130,
      render: (text: string | null | undefined) => (
        text ? <span style={{ fontFamily: 'monospace' }}>{text}</span> : '-'
      ),
    },
    {
      title: 'Device ID',
      dataIndex: 'device_id',
      key: 'device_id',
      width: 200,
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text}>
          <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
            {text.substring(0, 16)}...
          </span>
        </Tooltip>
      ),
    },
    {
      title: '首次访问',
      dataIndex: 'first_seen_at',
      key: 'first_seen_at',
      width: 160,
      render: (time: string) => formatBeijingTimeShort(time),
    },
    {
      title: '最后访问',
      dataIndex: 'last_seen_at',
      key: 'last_seen_at',
      width: 160,
      render: (time: string) => formatBeijingTimeShort(time),
    },
    {
      title: '近2天请求',
      key: 'daily_requests',
      width: 120,
      align: 'center',
      render: (_, record) => {
        const current = record.current_day_requests || 0;
        const limit = record.effective_limit || record.custom_daily_limit || 100;
        const percentage = (current / limit) * 100;
        
        let color = 'green';
        if (percentage >= 90) color = 'red';
        else if (percentage >= 70) color = 'orange';
        
        return (
          <Tag color={color}>
            {current} / {limit}
          </Tag>
        );
      },
    },
    {
      title: '总请求',
      dataIndex: 'total_requests',
      key: 'total_requests',
      width: 100,
      align: 'center',
    },
    {
      title: 'App 版本',
      dataIndex: 'app_version',
      key: 'app_version',
      width: 150,
      render: (version: string | null) => version || '-',
    },
    {
      title: '设备',
      dataIndex: 'device_model',
      key: 'device_model',
      width: 100,
      render: (model: string | null) => model || '-',
    },
    {
      title: '位置',
      key: 'location',
      width: 200,
      render: (_, record) => {
        const parts = [];
        if (record.city) parts.push(record.city);
        if (record.region) parts.push(record.region);
        if (record.country) parts.push(record.country);
        return parts.length > 0 ? parts.join(', ') : '-';
      },
    },
    {
      title: 'IP地址',
      dataIndex: 'last_ip',
      key: 'last_ip',
      width: 140,
      render: (ip: string | null) => ip || '-',
    },
    {
      title: '客户端积分',
      key: 'subscription',
      width: 120,
      align: 'center',
      render: (_, record) => {
        if (record.subscription_quota > 0) {
          const typeLabel = record.subscription_type === 'weekly' ? '周' : record.subscription_type === 'monthly' ? '月' : '';
          return (
            <Tag color="blue">
              {record.subscription_quota} {typeLabel && `(${typeLabel})`}
            </Tag>
          );
        }
        return <Tag color="default">0</Tag>;
      },
    },
    {
      title: '账本积分',
      key: 'credit_available',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <Tag color={(record.credit_available || 0) > 0 ? 'green' : 'default'}>
          {record.credit_available ?? 0}
        </Tag>
      ),
    },
    {
      title: '积分异常',
      key: 'credit_anomaly',
      width: 140,
      align: 'center',
      render: (_, record) => {
        if (!record.credit_anomaly) {
          return <Tag>正常</Tag>;
        }
        const flags = record.anomaly_flags?.length ? record.anomaly_flags.join(',') : 'flagged';
        return <Tag color="orange">{flags}</Tag>;
      },
    },
    {
      title: '状态',
      dataIndex: 'is_blocked',
      key: 'is_blocked',
      width: 80,
      align: 'center',
      render: (isBlocked: boolean) => (
        <Tag color={isBlocked ? 'red' : 'green'} icon={isBlocked ? <StopOutlined /> : <CheckCircleOutlined />}>
          {isBlocked ? '封禁' : '正常'}
        </Tag>
      ),
    },
    {
      title: '备注',
      dataIndex: 'notes',
      key: 'notes',
      width: 150,
      ellipsis: true,
      render: (notes: string | null) => notes || '-',
    },
    {
      title: '操作',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => navigate(`/users/${record.device_id}`)}
        >
          详情
        </Button>
      ),
    },
  ];

  return (
    <div>
      {/* 页面标题 */}
      <div style={{ marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>
          用户管理
        </Title>
      </div>

      {/* 搜索和筛选栏 */}
      <Space style={{ marginBottom: 16, flexWrap: 'wrap' }} size={[8, 8]}>
        <Input
          placeholder="搜索 User ID / Device ID"
          prefix={<SearchOutlined />}
          style={{ minWidth: 200 }}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onPressEnter={handleSearch}
          allowClear
        />
        <Select
          placeholder="选择国家"
          style={{ minWidth: 120 }}
          value={country}
          onChange={setCountry}
          allowClear
        >
          <Select.Option value="US">US</Select.Option>
          <Select.Option value="CN">CN</Select.Option>
          <Select.Option value="JP">JP</Select.Option>
          <Select.Option value="UK">UK</Select.Option>
        </Select>
        <Select
          placeholder="状态"
          style={{ minWidth: 100 }}
          value={isBlocked}
          onChange={setIsBlocked}
          allowClear
        >
          <Select.Option value="false">正常</Select.Option>
          <Select.Option value="true">封禁</Select.Option>
        </Select>
        <Select
          placeholder="积分异常"
          style={{ minWidth: 120 }}
          value={creditAnomaly}
          onChange={(v) => { setCreditAnomaly(v); setPage(1); }}
          allowClear
        >
          <Select.Option value="true">仅异常</Select.Option>
          <Select.Option value="false">无异常</Select.Option>
        </Select>
        <Button icon={<SearchOutlined />} type="primary" onClick={handleSearch}>
          搜索
        </Button>
        <Button icon={<ReloadOutlined />} onClick={loadUsers}>
          刷新
        </Button>
      </Space>

      {/* 数据表格 */}
      <Table
        columns={columns}
        dataSource={users}
        rowKey="fingerprint"
        loading={loading}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 个用户`,
          onChange: (newPage, newPageSize) => {
            setPage(newPage);
            setPageSize(newPageSize);
          },
        }}
        scroll={{ x: 1400 }}
      />
    </div>
  );
};

export default UserList;
